import { Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, ClipboardList } from "lucide-react";

import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import { isAccepting } from "./logic";
import type { FeedbackForm } from "./schemas";
import { useOpenFeedbackForms, useOwnSubmission } from "./use-feedback";

/**
 * The event page's way into feedback: every form taking answers right now.
 * Renders nothing when there is none, so an event without feedback looks
 * exactly as it did before the feature.
 */
export function EventFeedbackCard({
  eventId,
  slug,
  uid,
}: {
  eventId: string;
  slug: string;
  uid: string;
}) {
  const forms = useOpenFeedbackForms(eventId);
  if (forms.status === "error") {
    return (
      <Card data-testid="event-feedback-error" className="border-lit-card-edge">
        <CardContent className="flex items-start gap-2 py-4 text-sm text-muted-foreground">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0">
            Feedback forms could not be loaded. Reload the page to try again.
          </span>
        </CardContent>
      </Card>
    );
  }
  if (forms.status !== "success") return null;
  const accepting = forms.data
    .filter((form) => isAccepting(form))
    .sort((a, b) => a.title.localeCompare(b.title));
  if (accepting.length === 0) return null;

  return (
    <Card
      data-testid="event-feedback"
      className="border-lit-card-edge shadow-lit-card"
    >
      <CardContent className="space-y-3 py-4">
        <p className="flex items-center gap-2 font-medium text-foreground">
          <ClipboardList className="h-5 w-5 shrink-0 text-lit" />
          Feedback
        </p>
        <ul className="space-y-2">
          {accepting.map((form) => (
            <FeedbackRow
              key={form.id}
              eventId={eventId}
              slug={slug}
              uid={uid}
              form={form}
            />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function FeedbackRow({
  eventId,
  slug,
  uid,
  form,
}: {
  eventId: string;
  slug: string;
  uid: string;
  form: FeedbackForm;
}) {
  const submission = useOwnSubmission(eventId, form.id, uid);
  const done =
    submission.status === "success" &&
    (submission.data.status === "SUCCEEDED" ||
      submission.data.status === "QUEUED");
  // An anonymous answer is final, so there is nothing left to open.
  const final = done && form.anonymous;

  return (
    <li
      className="flex min-w-0 flex-wrap items-center gap-3"
      data-testid={`event-feedback-${form.id}`}
    >
      <span className="min-w-0 flex-1 wrap-anywhere text-sm text-foreground">
        {form.title}
      </span>
      {final ? (
        <span className="inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-lit" />
          Submitted
        </span>
      ) : (
        <Button
          asChild
          variant={done ? "outline" : "cta"}
          className="h-11 shrink-0"
        >
          <Link
            to="/e/$slug/feedback/$formId"
            params={{ slug, formId: form.id }}
            data-testid={`open-feedback-${form.id}`}
          >
            {done ? "Edit feedback" : "Give feedback"}
          </Link>
        </Button>
      )}
    </li>
  );
}
